/**
 * Locale resolution for the generated service-area index.
 *
 * WHY THIS EXISTS
 * ---------------
 * `cms_service_areas` stores its text three times (`title_zhHant` /
 * `title_zhHans` / `title_en`) and the public API collapses them per
 * request locale, exactly like `cms_products`. The build-time sync used to
 * fetch the index once with `?locale=zh-Hant`, so the generated file
 * carried Chinese copy only — the four cards on the About page showed
 * Chinese titles on the English and Simplified Chinese pages.
 *
 * The sync now fetches all three locales and merges them into
 * `entry.locales`; this module is the front-end half of that.
 *
 * THE TWO SHAPES
 * --------------
 *   `CmsServiceAreaIndexEntry` — the raw generated row. Its flat `title` /
 *                                 `description` mirror zh-Hant.
 *   `LocalizedServiceArea`      — the same row with the requested locale's
 *                                 copy spread over the flat fields, so a
 *                                 renderer can keep reading `area.title`.
 *
 * DEFENSIVE ABOUT A STALE GENERATED FILE
 * --------------------------------------
 * A generated file written by an older sync has no `locales` key at all.
 * The page then falls back to the flat zh-Hant fields rather than failing
 * to compile — a degraded but correct Chinese page, never a broken build.
 * That is the same contract `productLocale.ts` has.
 */

import { cmsServiceAreas, type CmsServiceAreaIndexEntry } from './service-areas.generated'
import { DEFAULT_LOCALE, type Locale } from '@/utils/localePath'

/** One locale's copy of a service area's text fields. */
export interface ServiceAreaLocaleCopy {
  title: string
  description: string
}

/** A generated row with one locale's copy resolved onto its flat fields. */
export type LocalizedServiceArea = Omit<CmsServiceAreaIndexEntry, 'locales'> &
  ServiceAreaLocaleCopy

/** The `locales` map, as the locale-aware sync writes it. */
type ServiceAreaLocales = Partial<Record<Locale, ServiceAreaLocaleCopy>>

/**
 * The copy for one locale of a generated row.
 *
 * Falls back to zh-Hant when the requested locale has no entry — the
 * generated file always carries all three, but a file written by an older
 * sync carries none, and the flat fields are the zh-Hant copy.
 */
export function serviceAreaCopy(
  area: CmsServiceAreaIndexEntry,
  locale: Locale,
): ServiceAreaLocaleCopy {
  const locales = (area as { locales?: ServiceAreaLocales }).locales
  const copy = locales?.[locale] ?? locales?.[DEFAULT_LOCALE]
  if (copy) return copy

  // A generated file with no `locales` map: the flat fields are zh-Hant.
  return { title: area.title, description: area.description }
}

/** One generated row, resolved for `locale`. */
export function localizeServiceArea(
  area: CmsServiceAreaIndexEntry,
  locale: Locale,
): LocalizedServiceArea {
  const { locales: _locales, ...rest } = area as CmsServiceAreaIndexEntry & {
    locales?: ServiceAreaLocales
  }
  return { ...rest, ...serviceAreaCopy(area, locale) }
}

/** The whole generated index, resolved for `locale`. */
export function localizeServiceAreas(
  areas: CmsServiceAreaIndexEntry[],
  locale: Locale,
): LocalizedServiceArea[] {
  return areas.map((a) => localizeServiceArea(a, locale))
}

/** The whole generated index, resolved for the active locale. */
export function localizedServiceAreas(locale: Locale): LocalizedServiceArea[] {
  return localizeServiceAreas(cmsServiceAreas, locale)
}
